import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BadgeEuro,
  Check,
  Crown,
  Layers3,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ease = [0.22, 1, 0.36, 1];

const sectionReveal = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.06,
    },
  },
};

const itemReveal = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease },
  },
};

const softRise = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.65, ease },
  },
};

const pricingOptions = [
  {
    id: 'one-off',
    title: 'One-Off Website',
    price: 'From €990',
    description:
      'A one-time investment for businesses that want a professional custom website and full ownership of the final build.',
    features: [
      'Custom website tailored to your business',
      'Modern responsive design',
      'Contact forms and CTA sections',
      'SEO-friendly foundations',
      'Full ownership of the final website files/code',
      'Additional features available if needed',
    ],
    note:
      'Final pricing can increase depending on complexity and requirements, such as booking/calendar integrations, advanced functionality, custom pages, or other special features.',
    buttonLabel: 'Discuss One-Off Website',
    icon: BadgeEuro,
    featured: false,
    accent: 'from-white/12 via-white/6 to-transparent',
  },
  {
    id: 'membership',
    title: 'Membership',
    price: 'From €59/month',
    description:
      'An ongoing solution for businesses that want a modern website plus continued support, updates, and flexibility as the business grows.',
    features: [
      'Website design and build',
      'Ongoing support and maintenance',
      'Text/content updates handled for the client',
      'Easier changes over time',
      'Less technical stress',
      'Ideal for businesses that want long-term support',
    ],
    note:
      'Monthly pricing may increase depending on the level of functionality, integrations, and project requirements.',
    buttonLabel: 'Start Membership Conversation',
    icon: Crown,
    featured: true,
    badge: 'Recommended',
    accent: 'from-cyan-500/24 via-violet-500/14 to-transparent',
  },
];

const comparisonItems = [
  {
    title: 'One-Off Website',
    icon: ShieldCheck,
    points: [
      'You pay upfront',
      'You receive the completed website build created by our team',
      'You own the final website files/code',
      'Future updates or changes are separate unless agreed',
    ],
  },
  {
    title: 'Membership',
    icon: Sparkles,
    points: [
      'Lower starting entry point',
      'Continued support is included',
      'If you need small changes later, such as text edits or small updates, we handle that',
      'Better for businesses that want an ongoing website partner',
    ],
  },
];

const scopeItems = [
  'Booking/calendar integration',
  'Advanced forms',
  'Custom features',
  'Extra pages',
  'Special animations',
  'Third-party integrations',
  'More advanced business requirements',
];

const recommendationItems = [
  {
    title: 'One-Off Website',
    subtitle: 'Best for businesses that want to pay once and fully own the final website build.',
  },
  {
    title: 'Membership',
    subtitle: 'Best for businesses that want support, flexibility, and ongoing updates as their business changes.',
    featured: true,
  },
];

const heroHighlights = ['Two clear options', 'Premium build quality', 'Flexible around your scope'];

function AnimatedGlow({ className, duration, delay, shouldReduceMotion }) {
  return (
    <motion.div
      aria-hidden="true"
      className={className}
      animate={shouldReduceMotion ? undefined : { y: [0, -14, 0], x: [0, 10, 0], scale: [1, 1.05, 1] }}
      transition={
        shouldReduceMotion
          ? undefined
          : { duration, delay, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }
      }
    />
  );
}

export default function PricingPage() {
  const shouldReduceMotion = useReducedMotion();
  const hoverLift = shouldReduceMotion
    ? {}
    : { y: -6, scale: 1.01, transition: { duration: 0.25, ease } };

  return (
    <div className="pt-20 overflow-hidden">
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.05),_transparent_36%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.1)_0%,_transparent_34%,_transparent_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,_rgba(255,255,255,0.02)_1px,_transparent_1px),linear-gradient(180deg,_rgba(255,255,255,0.02)_1px,_transparent_1px)] bg-[size:44px_44px] opacity-25" />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={15}
            delay={0}
            className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-500/14 blur-[120px] sm:h-96 sm:w-96"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={19}
            delay={1.1}
            className="absolute right-[-4rem] top-24 h-56 w-56 rounded-full bg-violet-500/12 blur-[110px] sm:h-72 sm:w-72"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={17}
            delay={0.7}
            className="absolute bottom-0 left-[-4rem] h-52 w-52 rounded-full bg-cyan-400/10 blur-[100px] sm:h-64 sm:w-64"
          />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionReveal}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div variants={itemReveal} className="mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-300 backdrop-blur-sm">
              Premium website pricing, kept clear and practical
            </motion.div>

            <motion.h1
              variants={itemReveal}
              className="text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="pricing-headline"
            >
              Pricing
            </motion.h1>

            <motion.p
              variants={itemReveal}
              className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-300 sm:text-lg lg:text-xl"
            >
              Choose the website option that fits your business best. Whether you want a one-time website build or ongoing support through membership, we keep the process clear, flexible, and tailored to your business.
            </motion.p>

            <motion.div variants={sectionReveal} className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {heroHighlights.map((item) => (
                <motion.div
                  key={item}
                  variants={softRise}
                  className="rounded-full border border-white/10 bg-[#101010]/85 px-4 py-2 text-sm font-medium text-gray-200 shadow-[0_12px_32px_rgba(0,0,0,0.24)] backdrop-blur-sm"
                >
                  {item}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.03)_0%,_transparent_26%,_transparent_100%)]" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto mb-10 max-w-3xl text-center sm:mb-14 lg:mb-16"
          >
            <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
              Pricing Options
            </motion.p>
            <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Two clear ways to move forward
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            variants={sectionReveal}
            className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8"
          >
            {pricingOptions.map((option) => {
              const Icon = option.icon;

              return (
                <motion.article
                  key={option.id}
                  variants={softRise}
                  whileHover={hoverLift}
                  className={`group relative overflow-hidden rounded-[2rem] border p-6 shadow-[0_24px_70px_rgba(0,0,0,0.32)] backdrop-blur-sm sm:p-8 lg:p-9 ${
                    option.featured
                      ? 'border-cyan-400/40 bg-[linear-gradient(180deg,_rgba(12,19,24,0.95),_rgba(14,14,18,0.9))]'
                      : 'border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.025))]'
                  }`}
                  data-testid={`pricing-card-${option.id}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${option.accent}`} />
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                  <div className="relative flex h-full flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        {option.badge && (
                          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                            <Crown size={12} />
                            {option.badge}
                          </div>
                        )}
                        <h3 className="text-3xl font-bold text-white sm:text-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {option.title}
                        </h3>
                      </div>

                      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${option.featured ? 'bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_14px_30px_rgba(34,211,238,0.18)]' : 'bg-white/8'}`}>
                        <Icon size={26} className="text-white" />
                      </div>
                    </div>

                    <div className="mt-8">
                      <div className="text-sm uppercase tracking-[0.2em] text-gray-500">Starting from</div>
                      <div className="mt-2 text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {option.price}
                      </div>
                    </div>

                    <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-300 sm:text-lg">
                      {option.description}
                    </p>

                    <div className="mt-8 grid gap-3">
                      {option.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-black/10 px-4 py-3 text-sm text-gray-300 sm:text-[15px]">
                          <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${option.featured ? 'bg-cyan-500/15 text-cyan-300' : 'bg-white/8 text-white'}`}>
                            <Check size={15} />
                          </div>
                          <span className="leading-6">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-6 text-gray-400">
                      {option.note}
                    </div>

                    <div className="mt-8 pt-2">
                      <Link
                        to="/contact"
                        className={`${option.featured ? 'btn-primary' : 'btn-secondary'} group inline-flex w-full items-center justify-center gap-2`}
                      >
                        {option.buttonLabel}
                        <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="relative bg-[#0d0d0d] py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute right-0 top-12 h-64 w-64 rounded-full bg-cyan-500/8 blur-[120px]" />
          <div className="absolute left-0 bottom-0 h-56 w-56 rounded-full bg-violet-500/8 blur-[110px]" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto mb-10 max-w-3xl text-center sm:mb-14"
          >
            <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
              Compare The Approach
            </motion.p>
            <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What changes between the two options
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            variants={sectionReveal}
            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
          >
            {comparisonItems.map((item) => {
              const Icon = item.icon;

              return (
                <motion.article
                  key={item.title}
                  variants={softRise}
                  whileHover={hoverLift}
                  className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.055),_rgba(255,255,255,0.02))] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur-sm sm:p-8"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.13),_transparent_34%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="mb-6 flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_14px_28px_rgba(34,211,238,0.16)]">
                        <Icon size={22} className="text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {item.title}
                      </h3>
                    </div>

                    <div className="space-y-3">
                      {item.points.map((point) => (
                        <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/6 bg-black/10 px-4 py-3 text-sm leading-6 text-gray-300 sm:text-[15px]">
                          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-400" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="section-container relative z-10">
          <div className="grid items-start gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="max-w-xl"
            >
              <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
                Scope Matters
              </motion.p>
              <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Pricing depends on the level of functionality
              </motion.h2>
              <motion.p variants={itemReveal} className="mt-5 text-base leading-relaxed text-gray-400 sm:text-lg">
                More demanding projects will cost more. The final price depends on the amount of content, complexity, integrations, and the overall scope of the build.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {scopeItems.map((item) => (
                <motion.div
                  key={item}
                  variants={softRise}
                  whileHover={hoverLift}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] p-4 shadow-[0_14px_36px_rgba(0,0,0,0.22)] backdrop-blur-sm sm:p-5"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(6,182,212,0.12),_transparent_42%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_10px_24px_rgba(34,211,238,0.16)]">
                      <Wrench size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="text-base font-medium text-white">{item}</div>
                      <div className="mt-1 text-sm text-gray-500">Affects final scope and pricing</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#0d0d0d] py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-16 h-60 w-60 -translate-x-1/2 rounded-full bg-violet-500/10 blur-[120px]" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto mb-10 max-w-3xl text-center sm:mb-14"
          >
            <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
              Recommendation
            </motion.p>
            <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Who is each option best for?
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            variants={sectionReveal}
            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
          >
            {recommendationItems.map((item) => (
              <motion.article
                key={item.title}
                variants={softRise}
                whileHover={hoverLift}
                className={`relative overflow-hidden rounded-[1.75rem] border p-6 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur-sm sm:p-8 ${
                  item.featured
                    ? 'border-cyan-400/35 bg-[linear-gradient(180deg,_rgba(12,19,24,0.95),_rgba(14,14,18,0.9))]'
                    : 'border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.055),_rgba(255,255,255,0.02))]'
                }`}
              >
                <div className={`absolute inset-0 ${item.featured ? 'bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.16),_transparent_34%)]' : 'bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.08),_transparent_34%)]'}`} />
                <div className="relative">
                  {item.featured && (
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                      <Layers3 size={12} />
                      Best fit for ongoing support
                    </div>
                  )}
                  <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-gray-300 sm:text-lg">
                    {item.subtitle}
                  </p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="absolute left-1/2 top-10 h-56 w-56 -translate-x-1/2 rounded-full bg-cyan-500/12 blur-[110px]" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.025))] px-6 py-10 text-center shadow-[0_24px_70px_rgba(0,0,0,0.34)] backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(6,182,212,0.14),_transparent_34%)]" />

            <motion.h2
              variants={itemReveal}
              className="relative text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Not sure which option fits you best?
            </motion.h2>

            <motion.p
              variants={itemReveal}
              className="relative mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-300 sm:text-lg lg:text-xl"
            >
              We can help you choose the right option based on your business, goals, and feature requirements.
            </motion.p>

            <motion.div variants={softRise} className="relative mt-8">
              <Link to="/contact" className="btn-primary group inline-flex items-center justify-center gap-2">
                Start Your Project
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}